import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { sha256 } from "js-sha256";
import {
  colorBorder,
  colorCTAOrange,
  colorCTAOrangeHover,
  colorLandingBackground,
  colorSurface,
  colorTextMuted,
  colorTextPrimary,
  colorTextSuccess,
  radiusCard,
  radiusInput,
} from "src/design/tokens";
import { useAcceptStaffInvitation } from "src/data/organization/queries/staff";

interface FormState {
  firstName: string;
  lastName: string;
  phone: string;
  password: string;
  confirmPassword: string;
}

const INITIAL: FormState = {
  firstName: "",
  lastName: "",
  phone: "",
  password: "",
  confirmPassword: "",
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: radiusInput,
    backgroundColor: colorSurface,
    "& fieldset": { borderColor: colorBorder },
    "&:hover fieldset": { borderColor: "#C7C3BC" },
    "&.Mui-focused fieldset": { borderColor: colorCTAOrange },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: colorCTAOrange },
};

const StaffInvitationPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const [form, setForm] = useState<FormState>(INITIAL);
  const [validationError, setValidationError] = useState<string | null>(null);

  const { mutate, isPending, isSuccess, isError } = useAcceptStaffInvitation();

  const handleChange =
    (field: keyof FormState) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setValidationError(t("staffInvitation.errorInvalidLink"));
      return;
    }
    if (form.password !== form.confirmPassword) {
      setValidationError(t("staffInvitation.errorPasswordMismatch"));
      return;
    }
    setValidationError(null);
    mutate({
      token,
      firstName: form.firstName.trim(),
      lastName: form.lastName.trim(),
      phone: form.phone.trim(),
      password: sha256(form.password),
    });
  };

  return (
    <Grid item xs={12} sx={{ backgroundColor: colorLandingBackground }}>
      <Box sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="sm">
        <Box textAlign="center" mb={5}>
          <Typography
            component="h1"
            sx={{
              fontFamily: "Bogart-bold",
              fontWeight: 700,
              fontSize: { xs: 28, md: 38 },
              color: colorTextPrimary,
              mb: 1.5,
            }}
          >
            {t("staffInvitation.title")}
          </Typography>
          <Typography variant="body1" color={colorTextMuted}>
            {t("staffInvitation.subtitle")}
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            border: `1px solid ${colorBorder}`,
            borderRadius: radiusCard,
            backgroundColor: colorSurface,
          }}
        >
          {isSuccess ? (
            <Box textAlign="center">
              <Alert
                severity="success"
                sx={{
                  borderRadius: radiusInput,
                  "& .MuiAlert-icon": { color: colorTextSuccess },
                  mb: 3,
                  textAlign: "left",
                }}
              >
                {t("staffInvitation.success")}
              </Alert>
              <Button
                variant="contained"
                disableElevation
                onClick={() => navigate("/login")}
                sx={{
                  backgroundColor: colorCTAOrange,
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  py: 1.4,
                  px: 4,
                  fontSize: 15,
                  "&:hover": { backgroundColor: colorCTAOrangeHover },
                }}
              >
                {t("staffInvitation.goToLogin")}
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("staffInvitation.fields.firstName")}
                    value={form.firstName}
                    onChange={handleChange("firstName")}
                    required
                    size="small"
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t("staffInvitation.fields.lastName")}
                    value={form.lastName}
                    onChange={handleChange("lastName")}
                    required
                    size="small"
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t("staffInvitation.fields.phone")}
                    value={form.phone}
                    onChange={handleChange("phone")}
                    required
                    size="small"
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label={t("staffInvitation.fields.password")}
                    value={form.password}
                    onChange={handleChange("password")}
                    required
                    size="small"
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label={t("staffInvitation.fields.confirmPassword")}
                    value={form.confirmPassword}
                    onChange={handleChange("confirmPassword")}
                    required
                    size="small"
                    sx={inputSx}
                  />
                </Grid>
                {(validationError || isError) && (
                  <Grid item xs={12}>
                    <Alert severity="error" sx={{ borderRadius: radiusInput }}>
                      {validationError ?? t("staffInvitation.error")}
                    </Alert>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disableElevation
                    disabled={isPending}
                    sx={{
                      backgroundColor: colorCTAOrange,
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: 600,
                      borderRadius: "8px",
                      py: 1.4,
                      fontSize: 15,
                      "&:hover": { backgroundColor: colorCTAOrangeHover },
                      "&.Mui-disabled": { opacity: 0.6 },
                    }}
                  >
                    {t("staffInvitation.cta")}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          )}
        </Paper>
        </Container>
      </Box>
    </Grid>
  );
};

export default StaffInvitationPage;
